/**
 * Shared "match-key" lobby shell for board-game multiplayer pages.
 *
 * Provides three thin page components each game (Chess, Pente, Checkers)
 * mounts via a tiny per-game wrapper:
 *
 *   1. <MatchKeyLanding cfg={...} />
 *      Two-button page modelled on Wu-Pong's tournament screen:
 *      "HOST MATCH" → /games/{game}/host
 *      "JOIN MATCH" → /games/{game}/join
 *
 *   2. <MatchKeyHost cfg={...} />
 *      Form: display name + match name → POST creates room → navigates
 *      to /games/{game}/room/:key
 *
 *   3. <MatchKeyJoin cfg={...} />
 *      Form: display name + 4-character key → navigates to /room/:key.
 *      Below the form: auto-refreshing list of open matches that you
 *      can click to join (or spectate if both seats are taken).
 *
 * The cfg prop holds the per-game theme + endpoints:
 *   { gameId, gameName, tagline, palette, fonts, apiPath, routeBase }
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RefreshCw, Users } from "lucide-react";
import { API } from "@/lib/api";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";

// ---------- Shared chrome ----------

function Shell({ cfg, eyebrow, children, backTo, backLabel = "BACK" }) {
  const navigate = useNavigate();
  const { palette: P, fonts: F, gameName, tagline } = cfg;
  const isPente = cfg.gameId === "pente";
  const isKarnoffel = cfg.gameId === "karnoffel";
  const wrapStyle = isPente
    ? { backgroundColor: "#03020E", backgroundImage: "url('/pente/space-earth.jpg')",
        color: P.ink, fontFamily: F.body }
    : { background: P.bg, color: P.ink, fontFamily: F.body };
  return (
    <div
      className={`relative min-h-screen overflow-x-clip${isPente ? " pente-stage-bg" : ""}${isKarnoffel ? " karnoffel-scope" : ""}`}
      data-testid={`${cfg.gameId}-${cfg.pageId}-page`}
      style={wrapStyle}
    >
      {isPente && (
        <>
          <div className="pointer-events-none absolute inset-0 pente-bg-veil" aria-hidden="true" />
        </>
      )}
      <TopBar theme="studio" onBack={() => navigate(backTo)} backLabel={backLabel}
        testId={`${cfg.gameId}-${cfg.pageId}-back-button`} />
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: `${P.accent}26` }}>
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 px-6 md:px-12 pt-10 pb-16 max-w-4xl">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-3"
            style={{ color: P.accent, fontFamily: F.eyebrow || F.title }}>
            {eyebrow}
          </div>
          <h1
            className="mb-3"
            data-testid={`${cfg.gameId}-${cfg.pageId}-title`}
            style={{
              fontFamily: F.title, fontWeight: F.titleWeight ?? 700,
              color: P.ink, fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.05,
              letterSpacing: F.titleSpacing ?? "0.02em",
              textShadow: P.titleShadow,
            }}
          >
            {gameName}
          </h1>
          <div className="mb-8 text-base"
            style={{
              color: P.accent,
              fontFamily: F.tagline || F.body,
              fontStyle: F.taglineItalic ? "italic" : "normal",
            }}>
            {tagline}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}

// ---------- 1. Landing ----------

export function MatchKeyLanding({ cfg }) {
  const navigate = useNavigate();
  const { palette: P, fonts: F, gameId, routeBase, swapLandingOrder, blurb } = cfg;
  const cardStyle = {
    background: P.cardBg ?? P.panel,
    border: `2px solid ${P.border}`,
    boxShadow: P.cardShadow ?? "0 6px 24px rgba(0,0,0,0.45)",
    color: P.ink,
  };
  const hostBtn = (
    <button
      key="host"
      onClick={() => navigate(`${routeBase}/host`)}
      data-testid={`${gameId}-landing-host-button`}
      className="group flex items-center justify-between gap-4 px-6 py-6 text-left transition-all"
      style={cardStyle}
    >
      <span className="text-2xl" style={{ fontFamily: F.title, fontWeight: 700, color: P.ink, fontSize: "clamp(1.5rem, 2.6vw, 1.875rem)", letterSpacing: "0.04em" }}>
        HOST MATCH
      </span>
      <ArrowRight size={22} className="shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: P.accent }} />
    </button>
  );
  const joinBtn = (
    <button
      key="join"
      onClick={() => navigate(`${routeBase}/join`)}
      data-testid={`${gameId}-landing-join-button`}
      className="group flex items-center justify-between gap-4 px-6 py-6 text-left transition-all"
      style={cardStyle}
    >
      <span className="text-2xl" style={{ fontFamily: F.title, fontWeight: 700, color: P.ink, fontSize: "clamp(1.5rem, 2.6vw, 1.875rem)", letterSpacing: "0.04em" }}>
        JOIN MATCH
      </span>
      <ArrowRight size={22} className="shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: P.accent }} />
    </button>
  );
  const tutorialBtn = cfg.tutorialPath ? (
    <button
      key="tutorial"
      onClick={() => navigate(cfg.tutorialPath)}
      data-testid={`${gameId}-landing-tutorial-button`}
      className="group flex items-center justify-between gap-4 px-6 py-6 text-left transition-all"
      style={cardStyle}
    >
      <span className="text-2xl" style={{ fontFamily: F.title, fontWeight: 700, color: P.ink, fontSize: "clamp(1.5rem, 2.6vw, 1.875rem)", letterSpacing: "0.04em" }}>
        GO THROUGH TUTORIAL
      </span>
      <ArrowRight size={22} className="shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: P.accent }} />
    </button>
  ) : null;
  return (
    <Shell cfg={{ ...cfg, pageId: "landing" }} eyebrow="Multiplayer · Match-Key" backTo="/" backLabel="HOME">
      {blurb && (
        <p
          data-testid={`${gameId}-blurb`}
          className="w-full max-w-3xl mb-6 text-sm md:text-base"
          style={{
            color: P.muted || P.ink,
            fontFamily: F.tagline || F.body,
            fontStyle: F.taglineItalic ? "italic" : "normal",
            lineHeight: 1.55,
          }}
        >
          {blurb}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
        {swapLandingOrder ? [joinBtn, hostBtn] : [hostBtn, joinBtn]}
      </div>
      {tutorialBtn && (
        <div className="grid grid-cols-1 gap-4 w-full max-w-2xl mt-4">
          {tutorialBtn}
        </div>
      )}
    </Shell>
  );
}

// ---------- 2. Host ----------

export function MatchKeyHost({ cfg }) {
  const navigate = useNavigate();
  const { palette: P, fonts: F, gameId, apiPath, routeBase, nameKey } = cfg;
  const [name, setName] = useState("");
  const [matchName, setMatchName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(nameKey);
    if (saved) setName(saved);
  }, [nameKey]);

  const inputStyle = {
    background: P.inputBg, color: P.ink,
    border: `1px solid ${P.border}`, fontFamily: F.body,
  };
  const submit = async (e) => {
    e.preventDefault();
    const myName = name.trim().slice(0, 30) || cfg.defaultHostName || "Stranger";
    const matchN = matchName.trim().slice(0, 30) || `${cfg.gameName} Match`;
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API}${apiPath}/rooms`, { name: matchN });
      const key = res.data?.key;
      if (!key) throw new Error("No key returned");
      localStorage.setItem(nameKey, myName);
      navigate(`${routeBase}/room/${key}`);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to set the match.");
      setBusy(false);
    }
  };

  return (
    <Shell cfg={{ ...cfg, pageId: "host" }} eyebrow="Multiplayer · Host" backTo={routeBase} backLabel="LOBBY">
      <form onSubmit={submit}
        className="p-5 max-w-xl"
        style={{ background: P.panel, border: `1px solid ${P.border}` }}
      >
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: P.accent, fontFamily: F.eyebrow || F.title }}>
            Your display name
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 30))}
            placeholder={cfg.defaultHostName || "Stranger"}
            data-testid={`${gameId}-host-name-input`}
            className="w-full px-3 py-2"
            style={inputStyle}
          />
        </div>
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: P.accent, fontFamily: F.eyebrow || F.title }}>
            Match name
          </div>
          <input
            value={matchName}
            onChange={(e) => setMatchName(e.target.value.slice(0, 30))}
            placeholder={`${cfg.gameName} Match`}
            data-testid={`${gameId}-host-match-input`}
            className="w-full px-3 py-2"
            style={inputStyle}
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid={`${gameId}-host-create-button`}
          className="w-full px-4 py-2 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
          style={{
            background: P.ink, color: P.btnInk ?? "#1B1A18",
            border: `1px solid ${P.border}`, fontWeight: 700, fontFamily: F.title,
          }}
        >
          {busy ? "Setting up the match…" : <>Create &amp; share key <ArrowRight size={13} /></>}
        </button>
        {error && (
          <div className="mt-3 text-sm" style={{ color: "#FF8B8B" }} data-testid={`${gameId}-host-error`}>
            {error}
          </div>
        )}
      </form>
    </Shell>
  );
}

// ---------- 3. Join ----------

export function MatchKeyJoin({ cfg }) {
  const navigate = useNavigate();
  const { palette: P, fonts: F, gameId, apiPath, routeBase, nameKey, blackLabel = "Black", whiteLabel = "White" } = cfg;
  const [name, setName] = useState("");
  const [keyInput, setKeyInput] = useState("");
  const [error, setError] = useState(null);
  const [openMatches, setOpenMatches] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem(nameKey);
    if (saved) setName(saved);
  }, [nameKey]);

  const refreshList = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}${apiPath}/rooms`);
      setOpenMatches(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
    finally { setLoadingList(false); }
  };
  useEffect(() => {
    refreshList();
    const id = setInterval(refreshList, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const joinByKey = (k) => {
    if (!k) return;
    const myName = name.trim().slice(0, 30) || cfg.defaultJoinName || "Stranger";
    localStorage.setItem(nameKey, myName);
    navigate(`${routeBase}/room/${k.toUpperCase()}`);
  };
  const submit = (e) => {
    e.preventDefault();
    const k = keyInput.trim().toUpperCase();
    if (k.length === 4) joinByKey(k);
    else setError("Match-key is 4 characters.");
  };

  const inputStyle = {
    background: P.inputBg, color: P.ink,
    border: `1px solid ${P.border}`, fontFamily: F.body,
  };
  return (
    <Shell cfg={{ ...cfg, pageId: "join" }} eyebrow="Multiplayer · Join" backTo={routeBase} backLabel="LOBBY">
      <form onSubmit={submit}
        className="p-5 max-w-xl"
        style={{ background: P.panel, border: `1px solid ${P.border}` }}
      >
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: P.accent, fontFamily: F.eyebrow || F.title }}>
            Your display name
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 30))}
            placeholder={cfg.defaultJoinName || "Stranger"}
            data-testid={`${gameId}-join-name-input`}
            className="w-full px-3 py-2"
            style={inputStyle}
          />
        </div>
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: P.accent, fontFamily: F.eyebrow || F.title }}>
            Match-key
          </div>
          <input
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value.toUpperCase().slice(0, 4))}
            placeholder="ABCD"
            maxLength={4}
            data-testid={`${gameId}-join-key-input`}
            className="w-full px-3 py-2 tracking-[0.4em] uppercase text-center"
            style={{ ...inputStyle, letterSpacing: "0.4em", fontSize: 18 }}
          />
        </div>
        <button
          type="submit"
          disabled={keyInput.length !== 4}
          data-testid={`${gameId}-join-button`}
          className="w-full px-4 py-2 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
          style={{
            background: "transparent", color: P.accent,
            border: `1px solid ${P.border}`, fontWeight: 600, fontFamily: F.title,
            opacity: keyInput.length !== 4 ? 0.4 : 1,
            cursor: keyInput.length !== 4 ? "not-allowed" : "pointer",
          }}
        >
          Join match <ArrowRight size={13} />
        </button>
        {error && (
          <div className="mt-3 text-sm" style={{ color: "#FF8B8B" }} data-testid={`${gameId}-join-error`}>
            {error}
          </div>
        )}
      </form>

      <div className="mt-10">
        <div className="flex items-center mb-3">
          <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: P.accent, fontFamily: F.eyebrow || F.title }}>
            Open Matches
          </div>
          <div className="flex-1" />
          <button
            onClick={refreshList}
            data-testid={`${gameId}-refresh-list`}
            className="text-xs uppercase tracking-[0.3em] inline-flex items-center gap-1"
            style={{ color: P.muted, fontFamily: F.eyebrow || F.title }}
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        <div
          className="overflow-y-auto"
          style={{ background: P.panel, border: `1px solid ${P.border}`, maxHeight: 300 }}
          data-testid={`${gameId}-open-matches`}
        >
          {loadingList && openMatches.length === 0 ? (
            <div className="px-4 py-3 text-sm italic" style={{ color: P.muted }}>Loading…</div>
          ) : openMatches.length === 0 ? (
            <div className="px-4 py-3 text-sm italic" style={{ color: P.muted }}>
              No open matches.  Host one above.
            </div>
          ) : (
            <table style={{ width: "100%", fontSize: gameId === "karnoffel" ? 16 : 14 }}>
              <tbody>
                {openMatches.map((m) => {
                  // Seats vary per game ({B,W} for Pente / Checkers, {w,b} for Chess).
                  const seatVals = Object.values(m.seats || {});
                  const seated = seatVals.filter(Boolean).length;
                  const full = seated >= 2;
                  return (
                    <tr key={m.key} style={{ borderBottom: `1px solid ${P.border}33` }}>
                      <td style={{ padding: "8px 12px", color: P.ink, width: 80, letterSpacing: "0.3em", fontFamily: F.title }}>
                        {m.key}
                      </td>
                      <td style={{ padding: "8px 12px", color: P.ink, fontFamily: F.title }}>{m.name}</td>
                      <td style={{ padding: "8px 12px", color: P.muted, fontSize: gameId === "karnoffel" ? 16 : 14 }}>
                        <Users size={11} className="inline -mt-0.5 mr-1" />
                        {m.members} {full && <span style={{ marginLeft: 4 }}>(full)</span>}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <button
                          onClick={() => joinByKey(m.key)}
                          data-testid={`${gameId}-join-${m.key}`}
                          className="text-xs uppercase tracking-[0.2em]"
                          style={{
                            color: full ? P.muted : P.accent,
                            cursor: full ? "default" : "pointer",
                            fontFamily: F.title,
                          }}
                        >
                          {full ? "Spectate" : "Join"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-2 text-xs" style={{ color: P.muted, fontFamily: F.eyebrow || F.title }}>
          ({whiteLabel} & {blackLabel} are assigned in join order.)
        </div>
      </div>
    </Shell>
  );
}
